"use client";

import { AudioLines } from "lucide-react";
import { cn } from "@/lib/utils/cn";
import type { useRecording } from "@/features/recorder/hooks/use-recording";
import type { ReactNode, RefObject } from "react";
import { useTranslation } from "react-i18next";
import { MAX_RECORDING_SECONDS } from "@/features/recorder/hooks/use-recording-engine";
import { PreviewArea } from "./recorder/preview-area";
import { DeviceSelector } from "./recorder/device-selector";
import { DevicesChip } from "./recorder/devices-chip";
import { RecordButton } from "./recorder/record-button";
import { RecordingComplete } from "./recorder/recording-complete";
import { LiveTranscript } from "@/features/recorder/components/live-transcript";
import type { LiveParagraph } from "@/features/recorder/hooks/use-live-transcript";
import { UploadProgress } from "./recorder/upload-progress";
import type { S3UploadProgress } from "../s3-upload";

interface MediaRecorderControlsProps {
  recording: ReturnType<typeof useRecording>;
  mode?: "audio" | "video" | "screen";
  /** @deprecated Use `mode` instead. */
  isVideo?: boolean;
  onSubmit: () => void;
  isSubmitting?: boolean;
  recordingName: string;
  onRecordingNameChange: (name: string) => void;
  folderId: string;
  onFolderChange: (folderId: string) => void;
  sourceLanguage: string;
  onLanguageChange: (lang: string) => void;
  previewStream: MediaStream | null;
  hasPermission: boolean | null;
  onRequestPermission: () => void;
  videoQuality: string;
  onQualityChange: (q: string) => void;
  transcriptParagraphs?: LiveParagraph[];
  isTranscriptConnected?: boolean;
  isTranscriptComplete?: boolean;
  transcriptError?: string | null;
  isLiveTranscription?: boolean;
  transcriptReconnectStatus?: "idle" | "reconnecting" | "exhausted";
  /** Upload progress from S3 upload — displayed during isSubmitting. */
  uploadProgress?: S3UploadProgress | null;
  submitError?: string | null;
  /** Screen mode only — camera-as-overlay PiP toggle state. */
  withCameraOverlay?: boolean;
  onWithCameraOverlayChange?: (value: boolean) => void;
  cameraStream?: MediaStream | null;
  /** Audio output devices for speaker select. Empty = browser doesn't enumerate outputs. */
  audioOutputDevices?: { id: string; label: string }[];
  selectedOutputDevice?: string;
  onOutputDeviceChange?: (id: string) => void;
  /** Called when Android camera switch button is tapped. */
  onSwitchCamera?: () => void;
  /** Ref forwarded to the playback <audio>/<video> element for setSinkId wiring. */
  mediaRef?: RefObject<HTMLAudioElement | HTMLVideoElement | null>;
  /** Engine callbacks for wavesurfer mic plugin registration (audio mode only). */
  onAttachMic?: (plugin: { start: () => void; pause: () => void; play: () => void; stop: () => void }) => void;
  onDetachMic?: () => void;
  /** Extra settings shown under the start button before recording starts. */
  settingsSlot?: ReactNode;
  /** Folder named in the live transcription line while recording. */
  liveFolderName?: string;
  /** Recording type switch shown centred on the top row before recording. */
  modeSwitch?: ReactNode;
  className?: string;
}

export function MediaRecorderControls({
  recording,
  mode: modeProp,
  isVideo: isVideoProp = false,
  onSubmit,
  isSubmitting = false,
  recordingName,
  onRecordingNameChange,
  folderId,
  onFolderChange,
  sourceLanguage,
  onLanguageChange,
  previewStream,
  hasPermission,
  onRequestPermission,
  videoQuality,
  onQualityChange,
  transcriptParagraphs = [],
  isTranscriptConnected = false,
  isTranscriptComplete = false,
  transcriptError = null,
  isLiveTranscription = false,
  transcriptReconnectStatus = "idle",
  uploadProgress = null,
  submitError = null,
  withCameraOverlay = false,
  onWithCameraOverlayChange,
  cameraStream = null,
  audioOutputDevices = [],
  selectedOutputDevice = "",
  onOutputDeviceChange,
  onSwitchCamera,
  mediaRef,
  onAttachMic,
  onDetachMic,
  settingsSlot,
  liveFolderName,
  modeSwitch,
  className,
}: MediaRecorderControlsProps) {
  const { t } = useTranslation();
  const {
    isRecording,
    isPaused,
    isReadyToSubmit,
    elapsedTime,
    previewUrl,
    error,
    stream,
    audioDevices,
    videoDevices,
    selectedAudioDevice,
    selectedVideoDevice,
    setSelectedAudioDevice,
    setSelectedVideoDevice,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    discardRecording,
    resumeRecording: onResume,
  } = recording;

  const mode: "audio" | "video" | "screen" =
    modeProp ?? (isVideoProp ? "video" : "audio");

  const recordingState = isRecording ? (isPaused ? "paused" : "recording") : "idle";
  const activeStream = isRecording ? (stream ?? null) : (isReadyToSubmit ? null : previewStream);

  const liveTranscriptNode =
    ((isRecording && isLiveTranscription) || transcriptParagraphs.length > 0) ? (
      <LiveTranscript
        paragraphs={transcriptParagraphs}
        isConnected={isTranscriptConnected}
        isComplete={isTranscriptComplete}
        error={transcriptError}
        reconnectStatus={transcriptReconnectStatus}
        className="mx-auto max-h-64 w-full max-w-2xl"
      />
    ) : null;

  const hasMicBanner = Boolean(error?.startsWith("Microphone disconnected") || error?.startsWith("Microphone muted"));
  const showStartPanel = mode === "audio" && recordingState === "idle" && hasPermission !== false && !hasMicBanner;
  const recordButton = (
    <RecordButton
      state={recordingState}
      mode={mode}
      isLiveTranscription={isLiveTranscription}
      onRecord={() => {
        startRecording();
      }}
      onPause={pauseRecording}
      onResume={resumeRecording}
      onStop={() => {
        stopRecording();
      }}
      onDiscard={discardRecording}
    />
  );
  const micLabel =
    audioDevices.find((d) => d.deviceId === selectedAudioDevice)?.label ||
    audioDevices[0]?.label ||
    t("RECORD.MICROPHONE", "Microphone");

  return (
    <div className={cn("flex flex-col gap-4", className)}>
      {/* Mic disconnect/mute errors are handled by MicStatusBanner in PreviewArea; this shows other errors. */}
      {error &&
        !error.startsWith("Microphone disconnected") &&
        !error.startsWith("Microphone muted") && (
          <div
            className="rounded-lg border border-danger/20 bg-danger/10 p-4 text-sm text-danger"
            data-testid="recorder-error"
          >
            {error}
          </div>
        )}

      {isReadyToSubmit ? (
        <>
          <RecordingComplete
            elapsedTime={elapsedTime}
            mode={mode === "screen" ? "video" : mode}
            previewUrl={previewUrl}
            blob={recording.blob}
            recordingName={recordingName}
            onRecordingNameChange={onRecordingNameChange}
            folderId={folderId}
            onFolderChange={onFolderChange}
            sourceLanguage={sourceLanguage}
            onLanguageChange={onLanguageChange}
            onNewRecording={() => {
              discardRecording();
            }}
            onUpload={onSubmit}
            isSubmitting={isSubmitting}
            submitError={submitError}
            mediaRef={mediaRef}
            isSavedInBrowser={recording.autosave === "on"}
          />
          {liveTranscriptNode}
          {isSubmitting && uploadProgress && (
            <UploadProgress progress={uploadProgress} />
          )}
        </>
      ) : (
        <>
          <div className="relative flex min-h-9 items-center gap-2 lg:justify-center">
            {modeSwitch}
            <DevicesChip
              micLabel={micLabel}
              recordingState={recordingState}
              elapsedTime={elapsedTime}
              withCamera={mode !== "audio"}
              className="ml-auto shrink-0 lg:absolute lg:inset-y-0 lg:right-0 lg:flex lg:items-center"
            >
              <DeviceSelector
                mode={mode}
                microphones={audioDevices.map((d) => ({ id: d.deviceId, label: d.label }))}
                cameras={videoDevices.map((d) => ({ id: d.deviceId, label: d.label }))}
                selectedMic={selectedAudioDevice}
                onMicChange={setSelectedAudioDevice}
                selectedCamera={selectedVideoDevice}
                onCameraChange={setSelectedVideoDevice}
                selectedQuality={videoQuality}
                onQualityChange={onQualityChange}
                withCameraOverlay={withCameraOverlay}
                onWithCameraOverlayChange={onWithCameraOverlayChange}
                audioOutputDevices={audioOutputDevices}
                selectedOutputDevice={selectedOutputDevice}
                onOutputDeviceChange={onOutputDeviceChange}
                onSwitchCamera={onSwitchCamera}
              />
            </DevicesChip>
          </div>
          {!showStartPanel && (
            <PreviewArea
              mode={mode}
              recordingState={recordingState}
              hasPermission={hasPermission}
              onRequestPermission={onRequestPermission}
              elapsedTime={elapsedTime}
              stream={activeStream}
              isPaused={isPaused}
              selectedDeviceId={selectedAudioDevice || undefined}
              micDisconnectError={
                error?.startsWith("Microphone disconnected") ? error : null
              }
              micMutedError={
                error?.startsWith("Microphone muted") ? error : null
              }
              onResume={onResume}
              cameraStream={cameraStream}
              onAttachMic={onAttachMic}
              onDetachMic={onDetachMic}
            />
          )}
          {recordingState === "idle" ? (
            <div className="flex flex-col items-center gap-5" data-testid="recorder-start-panel">
              <div className="flex flex-col items-center gap-3">
                {recordButton}
                <div className="text-center">
                  <p className="text-sm font-medium text-foreground">
                    {t("UPLOAD.RECORD_BUTTON.START", "Start recording")}
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {t("RECORD.MAX_LENGTH_HINT", "Up to {{hours}} hours", { hours: MAX_RECORDING_SECONDS / 3600 })}
                  </p>
                </div>
              </div>
              {settingsSlot}
            </div>
          ) : (
            <div className="flex flex-col items-center gap-5">
              <p className="text-xs text-muted-foreground" data-testid="recorder-tab-hint">
                {recording.autosave === "failed"
                  ? t("UPLOAD.RECORD.AUTOSAVE_OFF", "Auto-save is off. Keep this tab open and upload when you finish.")
                  : t("RECORD.KEEP_TAB_OPEN", "Keep this tab open while you record")}
              </p>
              {recordButton}
              {isLiveTranscription && (
                <span
                  className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-border bg-muted/50 px-3 py-1 text-xs text-muted-foreground"
                  data-testid="recorder-live-pill"
                >
                  <AudioLines className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  <span className="font-medium text-foreground">
                    {t("RECORD.LIVE_TRANSCRIPTION_ON", "Live transcription on")}
                  </span>
                  {liveFolderName && (
                    <span className="truncate">
                      · {t("RECORD.SAVING_TO_FOLDER", "Saving to {{folder}}", { folder: liveFolderName })}
                    </span>
                  )}
                </span>
              )}
            </div>
          )}
          {liveTranscriptNode}
        </>
      )}
    </div>
  );
}
